import { Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { apiClient } from '@/lib/api'

type Row = {
  id: string
  description: string
  forkCount: number
  ratingAvg: number
  ratingCount: number
  publishedAt: string
  skill: {
    id: string
    name: string
    description: string
    domain: string
    riskLevel: string
    industry: string | null
    category: string | null
    tags: string[]
    version: string
  }
  publisher: { id: string; name: string; publisherVerified: boolean; verificationLabel: string | null }
}

export default function MarketplacePage() {
  const { data, isLoading } = useQuery({
    queryKey: ['marketplace'],
    queryFn: async () =>
      (await apiClient.get<{ data: Row[]; meta: { total: number } }>('/api/v1/marketplace')).data,
  })

  if (isLoading) return <p className="text-sm text-neutral-500">Loading…</p>

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-2xl font-semibold">Marketplace</h1>
        <p className="text-sm text-neutral-500">
          {data?.meta.total ?? 0} certified skills published by the community.
        </p>
      </div>
      {(data?.data ?? []).length === 0 ? (
        <div className="rounded border bg-white p-6 text-sm text-neutral-500">
          No published skills yet.
        </div>
      ) : (
        <div className="grid md:grid-cols-2 gap-3">
          {(data?.data ?? []).map((r) => (
            <Link
              key={r.id}
              to={`/marketplace/${r.id}`}
              className="rounded border bg-white p-4 hover:shadow-sm"
            >
              <div className="flex items-baseline justify-between">
                <h2 className="font-semibold text-neutral-900">{r.skill.name}</h2>
                <span className="text-xs text-neutral-500">v{r.skill.version}</span>
              </div>
              <p className="mt-1 text-sm text-neutral-600 line-clamp-2">{r.description}</p>
              <div className="mt-2 text-xs text-neutral-500 flex gap-3 flex-wrap">
                <span>{r.skill.domain}</span>
                <span>risk {r.skill.riskLevel}</span>
                <span>{r.forkCount} forks</span>
                <span>
                  {r.ratingCount > 0 ? `${r.ratingAvg.toFixed(1)}★ (${r.ratingCount})` : 'no ratings'}
                </span>
                <span>
                  by {r.publisher.name}
                  {r.publisher.publisherVerified ? ' ✓' : ''}
                </span>
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  )
}
